import React, { useCallback, useRef, useState } from "react";
import { Dropdown, DropdownProps, makeStyles, mergeClasses, Option, tokens, Tooltip } from "@fluentui/react-components";
import { PositioningShorthand } from "@fluentui/react-positioning";

const useStyles = makeStyles({
    dropdown: {
        minWidth: 0,
        width: "100%",
        "&&&": {
            "border-bottom-color": tokens.colorNeutralStrokeAccessible,
        },
    },
    dropdownHover: {
        "&&&:hover": {
            "border-color": tokens.colorNeutralStrokeAccessible,
        },
    },
    truncatedText: {
        overflowX: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
    },
});

const TruncatedText: React.FC<{ text: string; className: string }> = ({ text, className }) => {
    const textRef = useRef<HTMLSpanElement>(null);
    const [showTooltip, setShowTooltip] = useState(false);

    const handleMouseEnter = useCallback(() => {
        const el = textRef.current;
        if (el && el.scrollWidth > el.clientWidth) {
            setShowTooltip(true);
        }
    }, []);

    const handleMouseLeave = useCallback(() => {
        setShowTooltip(false);
    }, []);

    return (
        <Tooltip content={text} relationship="description" visible={showTooltip} appearance="inverted" withArrow>
            <span ref={textRef} className={className} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                {text}
            </span>
        </Tooltip>
    );
};

export interface NewformaDropdownOption {
    key: string;
    text: string;
}

export interface NewformaDropdownProps
    extends Pick<
        DropdownProps,
        "disabled" | "placeholder" | "value" | "selectedOptions" | "onOptionSelect" | "listbox"
    > {
    options: NewformaDropdownOption[];
    className?: string;
    "data-testid"?: string;
    positioning?: PositioningShorthand;
}

const NewformaDropdown: React.FC<NewformaDropdownProps> = ({
    options,
    className,
    placeholder,
    disabled,
    value,
    positioning,
    listbox,
    ...dropdownProps
}) => {
    const isDarkMode = document.querySelector("[data-theme]")?.getAttribute("data-theme") !== "default";
    const styles = useStyles();

    // If value is undefined but there are selectedOptions, derive display text from options
    // to prevent Fluent's Dropdown from applying placeholder styling on a valid selection
    const resolvedValue =
        value ??
        (dropdownProps.selectedOptions?.length
            ? options.find((o) => dropdownProps.selectedOptions!.includes(o.key))?.text
            : undefined);
    const displayText = resolvedValue || placeholder || "";

    return (
        <Dropdown
            className={mergeClasses(styles.dropdown, !disabled && styles.dropdownHover, className)}
            appearance={isDarkMode ? "filled-darker" : "outline"}
            placeholder={placeholder}
            value={resolvedValue}
            disabled={disabled}
            disableAutoFocus
            positioning={positioning}
            listbox={listbox}
            button={{
                children: <TruncatedText text={displayText} className={styles.truncatedText} />,
            }}
            {...dropdownProps}
        >
            {options.map((option) => (
                <Option key={option.key} value={option.key} text={option.text}>
                    <TruncatedText text={option.text} className={styles.truncatedText} />
                </Option>
            ))}
        </Dropdown>
    );
};

export default NewformaDropdown;
